import { useTranslation } from "react-i18next";

import { Typography, Arrange, Separator, Button, Spacer } from "@aviary";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import { l } from "@emerson/locales/i18n";
import { FlipperPriceBlur } from "@shared/components/FlipperPriceBlur/FlipperPriceBlur";
import type { WholesaleCartQueryData } from "@shared/data/queries/WholesaleCartQuery/WholesaleCart.query";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { EmersonCheckoutStartEntryPoint } from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCertifications } from "@shared/hooks/useCertifications/useCertifications";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { getCashValue } from "@shared/utils/getCashValue/getCashValue";

import { BecomeTaxExemptNudge } from "../BecomeTaxExemptNudge/BecomeTaxExemptNudge";
import { CertificationWarningBar } from "../CertificationWarningBar/CertificationWarningBar";
import { FreeShippingBar } from "../FreeShippingBar/FreeShippingBar";

import * as styles from "./CartBody.styles";

interface Props {
  numberOfCartItems: number;
  subtotal: number;
  cartData: WholesaleCartQueryData;
}

const CartBody = ({ numberOfCartItems, subtotal, cartData }: Props) => {
  const { t } = useTranslation();
  const { desktop } = useBreakpoints();
  const [isAuthCaptureWholesaleEnabled] = useFlippers("auth_capture_wholesale_lighthouse");

  const isMobile = desktop.lessThan;
  const { hasSubmittedCertifications, loading } = useCertifications();
  const logAnalyticsEvent = useAnalyticsLogger();

  const logStartCheckoutEvent = () => {
    const productCount = cartData.currentOffice.wholesaleCart.lineItems.length;
    const itemCount = cartData.currentOffice.wholesaleCart.lineItems.reduce(
      (sum, value) => sum + value.quantity,
      0
    );
    const skuList = cartData.currentOffice.wholesaleCart.lineItems.map(
      lineItem => lineItem.variant.sku
    );

    const statusOfProducts = cartData.currentOffice.wholesaleCart.lineItems.map(
      lineItem => lineItem.variant.availabilityStatus
    );

    logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_CHECKOUT_START,
      payload: {
        entry_point: EmersonCheckoutStartEntryPoint.CART_DROPDOWN_HEADER,
        order_id: cartData.currentOffice.wholesaleCart.id,
        number_of_items: itemCount,
        number_of_products: productCount,
        sku_list: skuList,
        status_of_products: statusOfProducts,
        auth_and_capture: isAuthCaptureWholesaleEnabled,
        contains_hemp: cartData.currentOffice.wholesaleCart.lineItems.some(
          lineItem => lineItem.variant.product.containsCbd
        ),
      },
    });
  };

  const renderWarnings = () =>
    hasSubmittedCertifications ? (
      <BecomeTaxExemptNudge numberOfCartItems={numberOfCartItems} />
    ) : (
      <CertificationWarningBar />
    );

  const viewCart = (
    <Button
      css={styles.disabled}
      isFullWidth
      to={emersonRoutes.checkout.review}
      onClick={logStartCheckoutEvent}
      disabled={!hasSubmittedCertifications || loading || !numberOfCartItems}
      isText
      intention="textSystem"
    >
      {t(l.cart.ViewCart)}
    </Button>
  );

  const goToCheckoutLink = numberOfCartItems > 0 && (
    <Button
      isFullWidth
      to={emersonRoutes.checkout.shipping}
      onClick={logStartCheckoutEvent}
      disabled={!hasSubmittedCertifications || loading}
      data-e2e="go-to-checkout-button"
    >
      {t(l.cart.GoToCheckout)}
    </Button>
  );

  return (
    <Arrange isWrapped={false} isVertical spacing="xsmall" isFullWidth css={styles.container}>
      <Arrange spacing="xsmall" css={styles.wrapper.base} justify="center" alignment="center">
        <Typography type="h4">{numberOfCartItems}</Typography>
        <Typography type="h5">{t(l.cart.item_added, { count: numberOfCartItems })}</Typography>
      </Arrange>

      <Arrange
        spacing="xsmall"
        justify="spaceBetween"
        css={[styles.wrapper.base, styles.wrapper.muted]}
      >
        <Typography isMarginless>{t(l.cart.Subtotal)}</Typography>
        <Typography type="h5" isMarginless>
          <FlipperPriceBlur>{getCashValue(subtotal)}</FlipperPriceBlur>
        </Typography>
      </Arrange>

      {!!isMobile && <Spacer height="half" />}
      <FreeShippingBar numberOfCartItems={numberOfCartItems} subtotal={subtotal} />

      <Separator css={styles.separator} />

      {renderWarnings()}
      {viewCart}
      {goToCheckoutLink}
    </Arrange>
  );
};

export { CartBody };
